export interface TotalIssueFacts {
  /** Caller has determined the entered total disagrees with Supply + Distribution beyond tolerance. */
  isMismatch: boolean;
  /** Caller has determined the total field failed validation (missing, zero, negative, NaN). */
  isInvalid: boolean;
  totalAmount: number;
  calculatedTotal: number;
  /** Signed: positive when the entered total exceeds Supply + Distribution, negative when below. */
  diff: number;
}

export interface TotalIssueMessaging {
  hasIssue: boolean;
  iconGlyph: 'warning' | 'error' | '';
  tooltip: string;
  explanation: string;
  supplyActionLabel: string;
  supplyActionAmount: string;
  distributionActionLabel: string;
  distributionActionAmount: string;
  recalculateActionLabel: string;
  recalculateActionAmount: string;
}

const INVALID_TOOLTIP = 'Enter a valid total billing amount.';

function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function buildTotalIssueMessaging(facts: TotalIssueFacts): TotalIssueMessaging {
  const empty: TotalIssueMessaging = {
    hasIssue: false,
    iconGlyph: '',
    tooltip: '',
    explanation: '',
    supplyActionLabel: '',
    supplyActionAmount: '',
    distributionActionLabel: '',
    distributionActionAmount: '',
    recalculateActionLabel: '',
    recalculateActionAmount: '',
  };

  if (!facts.isMismatch && !facts.isInvalid) {
    return empty;
  }

  // An invalid total must be corrected before a mismatch can be reconciled,
  // so the fix section is shown only for a pure mismatch.
  const showFix = facts.isMismatch && !facts.isInvalid;
  const fmtTotal = formatCurrency(facts.totalAmount);
  const fmtCalculated = formatCurrency(facts.calculatedTotal);
  const fmtDiff = formatCurrency(Math.abs(facts.diff));
  const chargesTooLow = facts.diff >= 0;

  // The invalid state is more severe, so it owns the icon glyph and tooltip.
  const iconGlyph: 'warning' | 'error' = facts.isInvalid ? 'error' : 'warning';
  const tooltip = facts.isInvalid
    ? INVALID_TOOLTIP
    : `This total doesn't match Supply + Distribution (${fmtCalculated}). See below to reconcile the ${fmtDiff} difference.`;

  if (!showFix) {
    return { ...empty, hasIssue: true, iconGlyph, tooltip };
  }

  const signedDiff = chargesTooLow ? `+${fmtDiff}` : `-${fmtDiff}`;

  return {
    hasIssue: true,
    iconGlyph,
    tooltip,
    explanation: `These numbers don't add up. Your total is ${fmtTotal}, but Supply + Distribution comes to ${fmtCalculated} — a ${fmtDiff} gap.`,
    supplyActionLabel: chargesTooLow ? 'Add to Supply' : 'Remove from Supply',
    supplyActionAmount: signedDiff,
    distributionActionLabel: chargesTooLow ? 'Add to Distribution' : 'Remove from Distribution',
    distributionActionAmount: signedDiff,
    recalculateActionLabel: 'Set total to',
    recalculateActionAmount: fmtCalculated,
  };
}
